/* RichText — a single-paragraph field with REAL bold and a live preview.

   The poster engine reads one markup: plain text with <b>…</b> runs and HTML
   entities (&amp; &lt; &gt;). Authors used to type the tags by hand and were
   told to «write & as &amp;». This field shows bold as bold, toggles it with
   the B button or ⌘/Ctrl+B, and serialises the DOM back into EXACTLY that
   markup — nothing else can come out of it:

     • only <b> survives (strong / font-weight spans the browser may produce
       are folded into <b>; every other element is transparent);
     • text is entity-escaped (& < >), so the validator's «no '<' outside
       <b>» rule always holds;
     • Enter is swallowed (a bullet is one paragraph), paste is plain text,
       NBSP and newlines become spaces.

   Existing content is untouched until edited: the stored string renders as
   is (its <b> as bold, its entities as characters), and re-serialising an
   unedited field yields the same text. Uncontrolled like the textareas it
   replaces — the parent remounts the form on structural changes. */

import React, { useCallback, useEffect, useRef, useState } from 'react';

/** Stored markup → safe innerHTML: <b>/</b> pass through, a stray '<' is
    escaped, entities are left for the browser to decode. */
export function richToHtml(s: string): string {
  const parts = String(s ?? '').split(/(<\/?b>)/i);
  return parts
    .map((p, i) => (i % 2 === 1 ? p.toLowerCase() : p.replace(/</g, '&lt;')))
    .join('');
}

const BOLD_TAGS = new Set(['B', 'STRONG']);
const BLOCK_TAGS = new Set(['DIV', 'P', 'LI', 'BR']);

function escText(t: string): string {
  return t
    .replace(/\u00a0/g, ' ')
    .replace(/[\r\n]+/g, ' ')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function isBoldEl(el: HTMLElement): boolean {
  if (BOLD_TAGS.has(el.tagName)) return true;
  const fw = (el.style && el.style.fontWeight) || '';
  if (fw === 'bold' || fw === 'bolder') return true;
  const n = parseInt(fw, 10);
  return !isNaN(n) && n >= 600;
}

/** Editable DOM → stored markup (text + <b>…</b>, entity-escaped). */
export function htmlToRich(root: HTMLElement): string {
  const runs: Array<{ t: string; b: boolean }> = [];
  const push = (t: string, b: boolean) => {
    if (!t) return;
    const last = runs[runs.length - 1];
    if (last && last.b === b) last.t += t;
    else runs.push({ t, b });
  };
  const walk = (node: Node, bold: boolean) => {
    if (node.nodeType === Node.TEXT_NODE) {
      push(escText(node.nodeValue || ''), bold);
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;
    if (el.tagName === 'BR') {
      push(' ', bold);
      return;
    }
    const block = BLOCK_TAGS.has(el.tagName);
    if (block && runs.length) push(' ', bold);
    const b = bold || isBoldEl(el);
    el.childNodes.forEach((c) => walk(c, b));
    if (block) push(' ', bold);
  };
  root.childNodes.forEach((c) => walk(c, false));
  // A bold run that is only whitespace is not bold — never emit <b> </b>;
  // runs of spaces (block seams, NBSP pairs) collapse to one.
  return runs
    .map((r) => (r.b && r.t.trim() ? `<b>${r.t}</b>` : r.t))
    .join('')
    .replace(/<\/b><b>/g, '')
    .replace(/ {2,}/g, ' ')
    .replace(/ $/, '');
}

interface Props {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  /** Called once with the editable element after mount (focus hand-off). */
  onReady?: (el: HTMLDivElement) => void;
}

export default function RichText({ value, onChange, placeholder, onReady }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [boldOn, setBoldOn] = useState(false);

  // Uncontrolled: the stored markup is written once, on mount.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.innerHTML = richToHtml(value);
    lastRef.current = htmlToRich(el);
    if (onReady) onReady(el);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lastRef = useRef<string | null>(null);
  const emit = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const v = htmlToRich(el);
    if (v === lastRef.current) return; // blur without an edit changes nothing
    lastRef.current = v;
    onChange(v);
  }, [onChange]);

  const refreshBold = useCallback(() => {
    try {
      setBoldOn(document.queryCommandState('bold'));
    } catch {
      /* not in an editing host */
    }
  }, []);

  const toggleBold = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    // execCommand is legacy but universal in editing hosts and keeps the
    // native undo stack; whatever element the browser produces is folded
    // into <b> by htmlToRich.
    document.execCommand('bold');
    emit();
    refreshBold();
  }, [emit, refreshBold]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // one paragraph per field
      return;
    }
    if ((e.metaKey || e.ctrlKey) && (e.key === 'b' || e.key === 'B')) {
      e.preventDefault();
      toggleBold();
    }
  };

  const onPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain').replace(/[\r\n]+/g, ' ');
    if (!document.execCommand('insertText', false, text)) {
      const sel = window.getSelection();
      if (sel && sel.rangeCount) {
        const r = sel.getRangeAt(0);
        r.deleteContents();
        r.insertNode(document.createTextNode(text));
        r.collapse(false);
      }
    }
    emit();
  };

  return (
    <div className="arch-rich">
      <div
        ref={ref}
        className="arch-rich-ed"
        contentEditable
        suppressContentEditableWarning
        spellCheck
        data-placeholder={placeholder || ''}
        onInput={emit}
        onBlur={emit}
        onKeyDown={onKeyDown}
        onKeyUp={refreshBold}
        onMouseUp={refreshBold}
        onPaste={onPaste}
      />
      <button
        type="button"
        className={'arch-rich-b' + (boldOn ? ' on' : '')}
        title="Bold (⌘B / Ctrl+B)"
        aria-label="Bold"
        aria-pressed={boldOn}
        onMouseDown={(e) => e.preventDefault()} // keep the selection
        onClick={toggleBold}
      >
        B
      </button>
    </div>
  );
}
